import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Input,
  FormField,
  SubmitButton,
  SubmittingHint,
  Textarea,
  useSubmittingMutation,
} from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { emailFieldError, isValidEmail } from "@/lib/emailValidator";
import { isValidPhone, phoneFieldError } from "@/lib/phoneValidator";
import { US_STATES } from "@/lib/usStates";
import { PortalShell } from "../components/PortalShell";
import { brokerApi } from "../api/broker";
import { PortalApiError } from "../api/client";

// External Intake & Collection v1.0 PR 9b §8.1 — public broker
// application. Anonymous + rate-limited at /api/portal/broker/
// applications/submit.
//
// Series 5a (T3, OD-2): the submit response carries a single-use token so
// a fresh applicant can finish setting up immediately. Round-5 #2 — the
// token minted by BrokerApplicationService is a *set-password* token (the
// broker apply path moved to the mandatory-password flow), so we send the
// applicant to /portal/set-password, not the magic-link /portal/r redeem
// flow whose validate step rejects set-password tokens with HTTP 401. The
// "thanks, we'll email you" screen remains as a fallback for the
// dedup-on-resubmit case, where no token is returned.

interface FormState {
  companyName: string;
  contactName: string;
  contactEmail: string;
  phone: string;
  website: string;
  state: string;
  reason: string;
}

const empty: FormState = {
  companyName: "",
  contactName: "",
  contactEmail: "",
  phone: "",
  website: "",
  state: "",
  reason: "",
};

// Broker self-apply follow-ups — generous cap on the optional "how did you
// hear / why interested" free text; stored on Broker.PhoenixCapitalInterest.
const REASON_MAX_LENGTH = 2000;

export function BrokerApplyPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(empty);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const update = (k: keyof FormState) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  const contactEmailError = form.contactEmail
    ? emailFieldError(form.contactEmail, { required: true })
    : undefined;
  const phoneError = form.phone ? phoneFieldError(form.phone, { required: false }) : undefined;

  const application = useSubmittingMutation(
    async () => {
      const result = await brokerApi.submitApplication({
        companyName: form.companyName.trim(),
        contactName: form.contactName.trim(),
        contactEmail: form.contactEmail.trim(),
        phone: form.phone.trim() || undefined,
        website: form.website.trim() || undefined,
        state: form.state.trim() || undefined,
        reason: form.reason.trim() || undefined,
      });
      if (result.magicLinkToken) {
        navigate(`/portal/set-password?token=${encodeURIComponent(result.magicLinkToken)}`, {
          replace: true,
        });
        return;
      }
      setSubmitted(true);
    },
    {
      onError: (err) => {
        const msg = err instanceof PortalApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Could not submit the application. Please try again.";
        setError(msg);
      },
    },
  );
  const submitting = application.pending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.companyName.trim() || !form.contactName.trim() || !form.contactEmail.trim()) {
      setError("Company name, contact name, and email are required.");
      return;
    }
    if (!isValidEmail(form.contactEmail)) {
      setError("Enter a valid email address.");
      return;
    }
    if (form.phone.trim() && !isValidPhone(form.phone)) {
      setError("Enter a valid phone number.");
      return;
    }
    void application.submit();
  }

  if (submitted) {
    return (
      <PortalShell pageTitle="Application received">
        <h1 className="text-h2 font-display text-onyx m-0">Thanks — we've got your application</h1>
        <p className="text-small text-onyx mt-space-3">
          We already have an application in progress for this email. Check your
          inbox for the secure link we sent you, or open it from the email we
          sent originally.
        </p>
        <p className="text-small text-gray-mid mt-space-3">
          If you can't find the email, please reach out to support.
        </p>
      </PortalShell>
    );
  }

  return (
    <PortalShell pageTitle="Broker application">
      <h1 className="text-h2 font-display text-onyx m-0">Apply to become a Certified Broker</h1>
      <p className="text-small text-gray-mid mt-space-2">
        Tell us about your firm. We'll review your application and follow up by email.
      </p>

      <form onSubmit={handleSubmit} className="mt-space-5 flex flex-col gap-space-4" noValidate>
        <FormField label="Company name" required>
          <Input
            value={form.companyName}
            onChange={(e) => update("companyName")(e.target.value)}
            disabled={submitting}
            data-testid="broker-apply-company"
          />
        </FormField>
        <FormField label="Contact name" required>
          <Input
            value={form.contactName}
            onChange={(e) => update("contactName")(e.target.value)}
            disabled={submitting}
            data-testid="broker-apply-name"
          />
        </FormField>
        <FormField label="Contact email" required error={contactEmailError}>
          <Input
            type="email"
            inputMode="email"
            autoComplete="email"
            value={form.contactEmail}
            onChange={(e) => update("contactEmail")(e.target.value)}
            disabled={submitting}
            invalid={!!contactEmailError}
            data-testid="broker-apply-email"
          />
        </FormField>
        <FormField label="Phone" error={phoneError}>
          <Input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={form.phone}
            onChange={(e) => update("phone")(e.target.value)}
            disabled={submitting}
            invalid={!!phoneError}
          />
        </FormField>
        <FormField label="Website">
          <Input
            value={form.website}
            onChange={(e) => update("website")(e.target.value)}
            disabled={submitting}
          />
        </FormField>
        <FormField label="State">
          <Select
            value={form.state}
            onChange={(e) => update("state")(e.target.value)}
            disabled={submitting}
          >
            <option value="">—</option>
            {US_STATES.map((s) => (
              <option key={s.code} value={s.code}>{s.name} ({s.code})</option>
            ))}
          </Select>
        </FormField>
        <FormField label="How did you hear about Phoenix Capital, and why are you interested in becoming a broker?">
          <Textarea
            value={form.reason}
            onChange={(e) => update("reason")(e.target.value)}
            disabled={submitting}
            rows={4}
            maxLength={REASON_MAX_LENGTH}
            data-testid="broker-apply-reason"
          />
        </FormField>

        {error && (
          <p className="text-small text-status-error m-0" role="alert">{error}</p>
        )}

        <SubmitButton
          type="submit"
          pending={submitting}
          pendingLabel="Submitting…"
          data-testid="broker-apply-submit"
        >
          Submit application
        </SubmitButton>
        <SubmittingHint pending={submitting} />
      </form>

      <p className="text-caption text-gray-mid mt-space-5">
        By submitting, you confirm the information you've provided is accurate.
        Phoenix Capital will never ask for sensitive account credentials by email.
      </p>
    </PortalShell>
  );
}
